import { useState } from "react";
import { Bell, Check, ChevronLeft, ChevronRight, Settings } from "lucide-react";
import { Link } from "react-router-dom";
import {
  useMarkNotificationRead,
  useNotifications,
  type Notification,
} from "../api/notifications";

function formatEvent(event: string) {
  return event
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function NotificationRow({ n, onMarkRead }: { n: Notification; onMarkRead: (id: string) => void }) {
  return (
    <div
      className={`flex items-start gap-3 border-b border-gray-100 px-5 py-3 last:border-0 dark:border-riot-navy-700 ${
        n.read ? "" : "bg-riot-gold-50/40 dark:bg-riot-navy-900/40"
      }`}
    >
      <div className="mt-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-riot-gold-100 text-riot-gold-500 dark:bg-riot-navy-700">
        <Bell size={12} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900 dark:text-white">{formatEvent(n.event)}</p>
        <p className="text-xs text-gray-600 dark:text-riot-navy-200">{n.message}</p>
        <p className="mt-1 text-[11px] text-gray-400 dark:text-riot-navy-400">
          {new Date(n.timestamp).toLocaleString()}
        </p>
      </div>
      {!n.read && (
        <button
          onClick={() => onMarkRead(n.id)}
          className="flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-[11px] text-gray-600 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
        >
          <Check size={12} /> Mark read
        </button>
      )}
    </div>
  );
}

const PAGE_SIZE = 25;

export function NotificationsPage() {
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const currentCursor = cursorStack[cursorStack.length - 1];
  const { data, isLoading, error } = useNotifications({
    limit: PAGE_SIZE,
    cursor: currentCursor,
  });
  const markRead = useMarkNotificationRead();
  const items = data?.data ?? [];
  const hasMore = !!data?.pagination.hasMore;
  const nextCursor = data?.pagination.cursor;
  const page = cursorStack.length + 1;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Notifications</h2>
          {data?.unread ? (
            <span className="rounded-full bg-riot-gold-500 px-2 py-0.5 text-[11px] font-medium text-riot-navy-900">
              {data.unread} unread
            </span>
          ) : null}
        </div>
        <Link
          to="/notifications/preferences"
          className="flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
        >
          <Settings size={14} /> Preferences
        </Link>
      </div>

      {error && (
        <p className="mb-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300">
          Failed to load notifications: {String((error as Error).message ?? error)}
        </p>
      )}

      <div className="rounded-lg border border-gray-200 bg-white dark:border-riot-navy-700 dark:bg-riot-navy-800">
        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-sm text-gray-400 dark:text-riot-navy-400">
            Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <Bell size={40} className="mx-auto mb-3 text-gray-300 dark:text-riot-navy-600" />
              <p className="text-sm text-gray-500 dark:text-riot-navy-300">No notifications yet</p>
              <p className="mt-1 text-xs text-gray-400 dark:text-riot-navy-400">
                You'll see alerts about tasks, devices, and system events here.
              </p>
            </div>
          </div>
        ) : (
          items.map((n) => <NotificationRow key={n.id} n={n} onMarkRead={(id) => markRead.mutate(id)} />)
        )}
      </div>

      {(page > 1 || hasMore) && (
        <div className="mt-3 flex items-center justify-end gap-2 text-xs text-gray-500 dark:text-riot-navy-300">
          <span>Page {page}</span>
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setCursorStack((s) => s.slice(0, -1))}
            className="flex items-center gap-1 rounded border border-gray-300 px-2 py-1 hover:bg-gray-50 disabled:opacity-30 dark:border-riot-navy-600 dark:hover:bg-riot-navy-700"
          >
            <ChevronLeft size={12} /> Previous
          </button>
          <button
            type="button"
            disabled={!hasMore || !nextCursor}
            onClick={() => nextCursor && setCursorStack((s) => [...s, nextCursor])}
            className="flex items-center gap-1 rounded border border-gray-300 px-2 py-1 hover:bg-gray-50 disabled:opacity-30 dark:border-riot-navy-600 dark:hover:bg-riot-navy-700"
          >
            Next <ChevronRight size={12} />
          </button>
        </div>
      )}
    </div>
  );
}
